import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Mail, ChevronDown, MessageSquareText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const ChatLeadsPanel = ({ adminKey }) => {
  const [leads, setLeads] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/crm/chat-leads`)
      .then(({ data }) => setLeads(data.leads || []))
      .catch(() => setErr("Failed to load chat leads."));
  }, [adminKey]);

  if (err) return <div className="text-red-400 text-sm" data-testid="chatleads-error">{err}</div>;
  if (!leads)
    return (
      <div className="flex items-center gap-3 text-zinc-400" data-testid="chatleads-loading">
        <Loader2 className="animate-spin" size={16} /> Loading chat leads…
      </div>
    );
  if (leads.length === 0)
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-zinc-500" data-testid="chatleads-empty">
        No chat leads yet. When a visitor shares their email with the chat agent, it appears here.
      </div>
    );

  return (
    <div className="space-y-4" data-testid="chatleads-panel">
      <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">
        {leads.length} lead{leads.length === 1 ? "" : "s"} captured by the chat agent
      </div>
      {leads.map((l) => (
        <div key={l.session_id} className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`chatlead-${l.session_id}`}>
          <div className="flex flex-wrap items-center gap-3 p-4">
            <div className="w-9 h-9 rounded-lg border border-[#E5C56F]/30 bg-[#E5C56F]/10 flex items-center justify-center text-[#E5C56F] shrink-0">
              <MessageSquareText size={16} strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="text-sm text-zinc-100 font-medium truncate" data-testid="chatlead-email">{l.email}</div>
              <div className="text-[11px] text-zinc-500">{fmt(l.created_at)}</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <a
                href={`mailto:${l.email}`}
                data-testid="chatlead-mailto"
                className="inline-flex items-center gap-1.5 rounded-full border border-purple-500/40 bg-purple-600/10 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] text-purple-200 hover:bg-purple-600/20 transition-colors"
              >
                <Mail size={11} /> Email
              </a>
              <button
                onClick={() => setOpenId(openId === l.session_id ? null : l.session_id)}
                data-testid="chatlead-toggle-transcript"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-100 transition-colors"
              >
                Transcript
                <ChevronDown size={12} className={`transition-transform ${openId === l.session_id ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>
          {openId === l.session_id && (
            <div className="border-t border-white/10 bg-black/20 p-4 space-y-2.5 max-h-80 overflow-y-auto" data-testid="chatlead-transcript">
              {(l.transcript || []).map((m, i) => (
                <div key={`${m.created_at || m.at || m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[75%] rounded-xl px-3.5 py-2 text-[12.5px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-purple-600/20 border border-purple-500/25 text-zinc-100"
                        : "bg-white/[0.04] border border-white/10 text-zinc-300"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {(!l.transcript || l.transcript.length === 0) && (
                <div className="text-[12px] text-zinc-500">Transcript expired (chat history is kept 90 days).</div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ChatLeadsPanel;
